import { isSimpleObject } from "../abstractions/reactive-children";
import {
	ensureInert,
	getProxyComputedKeys,
	isReactive,
} from "../abstractions/reactive-proxy";
import { isObject } from "../abstractions/reactive-utils";
import {
	CloneInertOptions,
	CloneInertResult,
} from "./reactive-deep-clone-types";

/**
 * Type alias for the `cloneInert` function.
 * This can be useful for passing the function as a parameter.
 */
export type CloneInert = typeof cloneInert;

/**
 * Creates a deep or shallow clone of a reactive object, array, Set, or Map,
 * returning an "inert" (non-reactive) version.
 *
 * This is useful for obtaining a snapshot of the reactive state at a specific moment
 * without any reactive overhead.
 *
 * @param object The reactive (or non-reactive) data structure to clone.
 * @param cloneInertOptions Options to control the cloning behavior (e.g., depth, exclusion of computed properties).
 * @returns An inert clone of the input. The exact type depends on the input and options.
 */
export const cloneInert = <T, Options extends CloneInertOptions>(
	object: T,
	cloneInertOptions?: Options
): CloneInertResult<T, Options> => {
	return cloneAny(
		object,
		new Map(),
		cloneInertOptions,
		false
	) as CloneInertResult<T, Options>;
};

const cloneAny = <T>(
	object: T,
	visited: Map<unknown, unknown>,
	cloneInertOptions?: CloneInertOptions,
	requireSimpleObject = true
): T => {
	if (
		!isReactive(object) &&
		((!isSimpleObject(object) && requireSimpleObject) ||
			!isObject(object) ||
			Object.isFrozen(object))
	) {
		return ensureInert(object);
	}

	const inertObject = ensureInert(object);

	if (!visited.has(inertObject)) {
		if (Array.isArray(inertObject)) {
			return cloneReactiveArray(
				object as unknown[],
				inertObject,
				visited,
				cloneInertOptions
			) as T;
		}

		if (inertObject instanceof Set) {
			return cloneReactiveSet(
				object as unknown as Set<unknown>,
				inertObject,
				visited,
				cloneInertOptions
			) as T;
		}

		if (inertObject instanceof Map) {
			return cloneReactiveMap(
				object as unknown as Map<unknown, unknown>,
				inertObject,
				visited,
				cloneInertOptions
			) as T;
		}

		return cloneReactiveObject(
			object,
			inertObject,
			visited,
			cloneInertOptions
		) as T;
	}

	return visited.get(inertObject) as T;
};

const cloneReactiveObject = <T extends object>(
	proxy: T,
	inertObject: T,
	visited: Map<unknown, unknown>,
	cloneInertOptions?: CloneInertOptions
) => {
	const newObject = {} as T;
	visited.set(inertObject, newObject);

	const ownKeys = Reflect.ownKeys(proxy) as Array<keyof T>;
	const computedKeys = getProxyComputedKeys(proxy) as Set<keyof T>;

	const keys =
		cloneInertOptions?.excludeComputed === true
			? new Set(ownKeys.filter((ownKey) => !computedKeys.has(ownKey)))
			: new Set([...ownKeys, ...computedKeys]);

	for (const key of keys) {
		newObject[key] = cloneAny(proxy[key], visited, cloneInertOptions);
	}

	return newObject;
};

const cloneReactiveMap = <T extends Map<unknown, unknown>>(
	proxy: T,
	inertObject: T,
	visited: Map<unknown, unknown>,
	cloneInertOptions?: CloneInertOptions
) => {
	const newMap = new Map() as unknown as T;
	visited.set(inertObject, newMap);

	for (const [key, value] of proxy) {
		const newKey =
			cloneInertOptions?.deep == false
				? key
				: cloneAny(key, visited, cloneInertOptions);

		const newValue =
			cloneInertOptions?.deep == false
				? value
				: cloneAny(value, visited, cloneInertOptions);

		newMap.set(newKey, newValue);
	}

	return newMap;
};

const cloneReactiveArray = <T extends unknown[]>(
	proxy: T,
	inertObject: T,
	visited: Map<unknown, unknown>,
	cloneInertOptions?: CloneInertOptions
): T => {
	const newArray = [] as unknown as T;
	visited.set(inertObject, newArray);

	for (let i = 0; i < proxy.length; i++) {
		newArray.push(
			cloneInertOptions?.deep === false
				? proxy[i]
				: cloneAny(proxy[i], visited, cloneInertOptions)
		);
	}

	return newArray;
};

const cloneReactiveSet = <T extends Set<unknown>>(
	proxy: T,
	inertObject: T,
	visited: Map<unknown, unknown>,
	cloneInertOptions?: CloneInertOptions
) => {
	const newSet = new Set() as unknown as T;
	visited.set(inertObject, newSet);

	for (const value of proxy) {
		newSet.add(
			cloneInertOptions?.deep === false
				? value
				: cloneAny(value, visited, cloneInertOptions)
		);
	}

	return newSet;
};
